import { useCallback, useEffect, useState } from 'react';
import config, { logger } from '../config';
import { getApiHeaders } from './api';

let cachedCatalog = null;
let inflight = null;
const listeners = new Set();

function notify() {
  listeners.forEach((listener) => listener(cachedCatalog));
}

export function getCachedContentCatalog() {
  return cachedCatalog;
}

/**
 * Shared product copy from GET /api/content.
 * One in-flight request is reused by every caller.
 *
 * @returns {Promise<object|null>}
 */
export function fetchContentCatalog() {
  if (cachedCatalog) return Promise.resolve(cachedCatalog);
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.CONTENT_CATALOG}`, {
        method: 'GET',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      });
      if (!response.ok) {
        logger.error('Failed to fetch content catalog, status:', response.status);
        return null;
      }
      const data = await response.json().catch(() => ({}));
      if (!data?.success || !data?.data || typeof data.data !== 'object') return null;
      cachedCatalog = data.data;
      notify();
      return cachedCatalog;
    } catch (error) {
      logger.error('Error fetching content catalog:', error);
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function resetContentCatalogCache() {
  cachedCatalog = null;
  inflight = null;
}

/**
 * Replace `{name}` placeholders in catalog templates.
 * Unknown placeholders are left empty.
 */
export function fillTemplate(template, values = {}) {
  return String(template ?? '').replace(/\{(\w+)\}/g, (_, key) => (
    values[key] == null ? '' : String(values[key])
  ));
}

export function financialProfileSectionTitles(override) {
  if (Array.isArray(override) && override.length) return override;
  const items = cachedCatalog?.financial_profile_sections;
  if (!Array.isArray(items)) return [];
  return items.map((item) => item.title).filter(Boolean);
}

export function financialProfileQuestionLabels(fields) {
  const list = Array.isArray(fields)
    ? fields
    : cachedCatalog?.financial_profile_fields?.fields;
  const labels = {};
  (Array.isArray(list) ? list : []).forEach((field) => {
    if (field?.id && field?.label) labels[field.id] = field.label;
  });
  return labels;
}

export function collegeYearRowLabel(childIndexZeroBased, template) {
  const pattern = template
    || cachedCatalog?.financial_profile_fields?.college_year_label
    || 'Child {n} college start year';
  return fillTemplate(pattern, { n: childIndexZeroBased + 1 });
}

export function useContentCatalog() {
  const [catalog, setCatalog] = useState(cachedCatalog);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!cachedCatalog);

  useEffect(() => {
    if (cachedCatalog) {
      setCatalog(cachedCatalog);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    fetchContentCatalog().then((result) => {
      if (!active) return;
      if (result) {
        setCatalog(result);
        setError('');
      } else {
        setError('Could not load content. Please try again.');
      }
      setLoading(false);
    });

    const onUpdate = (next) => {
      if (!active || !next) return;
      setCatalog(next);
      setError('');
      setLoading(false);
    };
    listeners.add(onUpdate);
    return () => {
      active = false;
      listeners.delete(onUpdate);
    };
  }, []);

  const retry = useCallback(() => {
    resetContentCatalogCache();
    setCatalog(null);
    setError('');
    setLoading(true);
    fetchContentCatalog().then((result) => {
      if (result) {
        setCatalog(result);
        setError('');
      } else {
        setError('Could not load content. Please try again.');
      }
      setLoading(false);
    });
  }, []);

  return { catalog, loading, error, retry };
}
